import {
  FieldMappings,
  CompositeMapping,
} from 'src/api/spreadsheet/workflow/useWorkflowMapFields';

/**
 * Applies field mappings to transform CSV data by fusing columns and applying mappings
 *
 * @param data - 2D array where first row is headers and subsequent rows are data
 * @param fieldMappings - Object containing division, product, and date mappings
 * @returns Transformed data with fused columns and applied mappings
 */
export function applyFieldMappings(
  data: unknown[][],
  fieldMappings: FieldMappings,
): unknown[][] {
  if (data.length === 0) return data;

  // Clone the data to avoid mutating the original
  let transformedData = data.map((row) => [...row]);

  // Apply required mappings (always present)
  transformedData = applyCompositeMapping(
    transformedData,
    fieldMappings.divisionMappings,
  );
  transformedData = applyCompositeMapping(
    transformedData,
    fieldMappings.productMappings,
  );

  // Apply optional mappings (only if present)
  if (fieldMappings.dateMappings) {
    transformedData = applyCompositeMapping(
      transformedData,
      fieldMappings.dateMappings,
    );
  }

  return transformedData;
}

/**
 * Applies a single composite mapping to the data
 * Step 1: Fuse source columns into target column
 * Step 2: Apply mappings to transform the fused values
 * Step 3: Delete original source columns
 */
function applyCompositeMapping(
  data: unknown[][],
  mapping: CompositeMapping,
): unknown[][] {
  if (data.length === 0) return data;

  const {
    sourceColumnNames,
    targetColumnName,
    mappings,
    separator = '|||',
  } = mapping;

  // Handle case where AI couldn't identify relevant source columns
  // In this case, we just add the target column with the universal mapping
  if (!sourceColumnNames || sourceColumnNames.length === 0) {
    // Only proceed if we have a universal mapping "*"
    if (!mappings['*']) {
      return data; // Skip if no universal mapping and no source columns
    }

    // Add target column with universal value for all rows
    const headers = data[0] as string[];
    const dataRows = data.slice(1);

    // Check if target column already exists
    const targetIndex = headers.findIndex(
      (header) => header === targetColumnName,
    );
    if (targetIndex !== -1) {
      return data; // Target column already exists, skip
    }

    // Add new target column header
    const newHeaders = [...headers, targetColumnName];
    const newTargetIndex = newHeaders.length - 1;

    // Add universal value to all data rows
    const newDataRows = dataRows.map((row) => {
      const newRow = [...row];
      // Ensure row has enough elements for new target column
      while (newRow.length <= newTargetIndex) {
        newRow.push('');
      }
      newRow[newTargetIndex] = mappings['*']; // Set universal value
      return newRow;
    });
    return [newHeaders, ...newDataRows] as unknown[][];
  }

  // Get headers (first row)
  const headers = data[0] as string[];
  const dataRows = data.slice(1);

  // Find indexes of source columns
  const sourceIndexes = sourceColumnNames
    .map((colName) => headers.findIndex((header) => header === colName))
    .filter((index) => index !== -1); // Remove columns that don't exist

  // Skip if no valid source columns found
  if (sourceIndexes.length === 0) {
    return data;
  }

  // Check if target column already exists, if not add it
  let targetIndex = headers.findIndex((header) => header === targetColumnName);
  const newHeaders = [...headers];

  if (targetIndex === -1) {
    // Add new target column
    newHeaders.push(targetColumnName);
    targetIndex = newHeaders.length - 1;
  }

  // Step 1: Fuse columns and create target column data
  const newDataRows = dataRows.map((row) => {
    const newRow = [...row];

    // Ensure row has enough elements for new target column
    while (newRow.length <= targetIndex) {
      newRow.push('');
    }

    // Fuse source column values
    const sourceValues = sourceIndexes.map((index) => {
      const value = row[index];
      return value ? String(value).trim() : '';
    });
    // Don't filter out empty values - we want to preserve them for universal mapping

    const fusedValue = sourceValues.join(separator);

    // Step 2: Apply mapping to the fused value
    // Priority 1: Exact match
    // Priority 2: Universal mapping "*" for any non-matched values (including empty)
    // Priority 3: Keep original value
    let mappedValue = fusedValue;
    if (mappings[fusedValue] !== undefined) {
      // Exact match found
      const mappingResult = mappings[fusedValue];
      // Normalize MappingValue to string (splits are handled server-side)
      if (typeof mappingResult === 'string') {
        mappedValue = mappingResult;
      } else if (mappingResult.type === 'simple') {
        mappedValue = mappingResult.value;
      }
      // For split mappings, keep original value (will be processed server-side)
    } else if (mappings['*'] !== undefined) {
      // No exact match found, but universal mapping exists - apply it to any value (including empty)
      const mappingResult = mappings['*'];
      // Normalize MappingValue to string
      if (typeof mappingResult === 'string') {
        mappedValue = mappingResult;
      } else if (mappingResult.type === 'simple') {
        mappedValue = mappingResult.value;
      }
    }
    // else: keep original fusedValue

    // Set the target column value
    newRow[targetIndex] = mappedValue;

    return newRow;
  });

  // Step 3: Remove original source columns (in reverse order to maintain indexes)
  const sourceIndexesToRemove = sourceIndexes
    .filter((index) => index !== targetIndex) // Don't remove target column if it was a source
    .sort((a, b) => b - a); // Sort in descending order

  // Remove columns from headers
  sourceIndexesToRemove.forEach((index) => {
    newHeaders.splice(index, 1);
  });

  // Remove columns from data rows
  const finalDataRows = newDataRows.map((row) => {
    const newRow = [...row];
    sourceIndexesToRemove.forEach((index) => {
      newRow.splice(index, 1);
    });
    return newRow;
  });

  // Return transformed data with new headers
  return [newHeaders, ...finalDataRows];
}
